import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { config } from '../config';

// Create HTTP link
const httpLink = createHttpLink({
  uri: config.graphqlEndpoint,
});

// Auth link to add Bearer token
const authLink = setContext((_, { headers }) => {
  // Get the authentication token from localStorage if it exists
  const token = localStorage.getItem('msal.token');
  console.log('🔑 Apollo AuthLink token:', token);
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    }
  };
});

// Phrases that indicate the server rejected the token as expired / invalid
const AUTH_EXPIRED_PHRASES = [
  'missing authentication claims',
  'please re-authenticate',
  'invalid credentials',
  'unauthenticated',
];

const isAuthExpiredMessage = (msg: string) =>
  AUTH_EXPIRED_PHRASES.some(p => msg.toLowerCase().includes(p));

/** Dispatched when a GraphQL or network 401 indicates the session has expired. */
export const AUTH_EXPIRED_EVENT = 'talkforty:authExpired';

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      if (isAuthExpiredMessage(message)) {
        console.warn('[Apollo] Auth-expired GraphQL error detected — dispatching authExpired event');
        localStorage.removeItem('msal.token');
        window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
      }
    });
  }

  if (networkError) {
    console.error(`Network error: ${networkError}`);
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      localStorage.removeItem('msal.token');
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
  }
});

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          categories: {
            merge: false, // Replace the array instead of merging
          },
          items: {
            merge: false,
          },
          hierarchyLevels: {
            merge: false, // Replace the array instead of merging
          },
          activeHierarchyLevels: {
            merge: false,
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});
